/* eslint-disable no-unused-vars */
import React, { useState, useMemo, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Image,
  TouchableOpacity,
  FlatList,
  TextInput,
  SafeAreaView,
  StatusBar,
  KeyboardAvoidingView,
  Platform,
  ActivityIndicator,
  RefreshControl,
} from 'react-native';
import { useNavigation, useFocusEffect } from '@react-navigation/native';
import { Ionicons, MaterialIcons, FontAwesome5 } from '@expo/vector-icons';
import useProfile from '../../hooks/useProfile';
import ProfileManagementService from '../../services/ProfileManagementService';
import PROFILE_KEYS from '../../constants/profileKeys';
import { useConversations } from '../../hooks/useChat';
// === API CONFIG ===
const API_BASE = 'https://api.jobeng.ai';
const API_KEY = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';

// Default avatar placeholder when the other party has no logo/avatar.
const defaultLogo = {
  uri: 'https://via.placeholder.com/100/0e4e96/FFFFFF?text=%20',
};

// Format an ISO timestamp into a compact inbox label.
function formatTimestamp(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const now = new Date();
  const sameDay  = d.toDateString() === now.toDateString();
  const yest     = new Date(now); yest.setDate(now.getDate() - 1);
  const isYest   = d.toDateString() === yest.toDateString();
  if (sameDay) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  if (isYest)  return 'Yesterday';
  const diffDays = Math.floor((now - d) / (1000 * 60 * 60 * 24));
  if (diffDays < 7) return d.toLocaleDateString([], { weekday: 'short' });
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

// Map an API conversation row → the shape the existing render item expects.
function mapToItem(c) {
  return {
    id:             String(c.id),
    company:        c.company_name || c.other_name || 'Conversation',
    position:       c.other_title || c.subject || '',
    logo:           c.company_logo
                      ? { uri: c.company_logo }
                      : (c.other_avatar ? { uri: c.other_avatar } : defaultLogo),
    lastMessage:    c.last_message_preview || '(No messages yet)',
    timestamp:      formatTimestamp(c.last_message_at),
    date:           c.last_message_at,
    unread:         (c.unread_count || 0) > 0,
    unreadCount:    c.unread_count || 0,
    otherUserId:    c.other_user_id,
    otherRole:      c.other_role,
    companyId:      c.company_id,
    raw:            c,
  };
}

export default function MessagesScreen() {
  // ── Profile integration ──
  const profileCtx = useProfile();
  const navigation = useNavigation();
  const [activeTab,   setActiveTab]   = useState('all');     // all, unread, archived
  const [searchQuery, setSearchQuery] = useState('');
  const [refreshing,  setRefreshing]  = useState(false);

  const { conversations, role, loading, error, refresh } = useConversations({ pollMs: 5000 });

  // Re-fetch when the screen gains focus too — covers the case where a user sent a
  // message in ChatDetailScreen and comes back here.
  useFocusEffect(useCallback(() => { refresh(); }, [refresh]));

  const items = useMemo(() => conversations.map(mapToItem), [conversations]);

  const filtered = useMemo(() => {
    let list = items;
    if (activeTab === 'unread')   list = list.filter(i => i.unread);
    if (activeTab === 'archived') list = list.filter(i => i.raw && i.raw.archived);
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(i =>
        (i.company || '').toLowerCase().includes(q) ||
        (i.position || '').toLowerCase().includes(q) ||
        (i.lastMessage || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [items, activeTab, searchQuery]);

  const unreadTotal = useMemo(() => items.filter(i => i.unread).length, [items]);

  const handlePullToRefresh = useCallback(async () => {
    setRefreshing(true);
    try { await refresh(); } finally { setRefreshing(false); }
  }, [refresh]);

  const handleConversationPress = (item) => {
    navigation.navigate('ChatDetail', {
      conversation: {
        id:         item.id,
        company:    item.company,
        position:   item.position,
        logo:       item.logo,
        otherRole:  item.otherRole,
        otherUserId: item.otherUserId,
        companyId:  item.companyId,
      },
    });
  };

  const renderConversationItem = ({ item }) => (
    <TouchableOpacity
      style={styles.conversationItem}
      onPress={() => handleConversationPress(item)}
    >
      <Image source={item.logo} style={styles.companyLogo} />

      <View style={styles.conversationContent}>
        <View style={styles.conversationHeader}>
          <Text style={styles.companyName}>{item.company}</Text>
          <Text style={styles.timestamp}>{item.timestamp}</Text>
        </View>

        {item.position ? (
          <Text style={styles.positionName}>{item.position}</Text>
        ) : null}

        <View style={styles.messageContainer}>
          <Text
            style={[styles.lastMessage, item.unread && styles.unreadMessage]}
            numberOfLines={2}
          >
            {item.lastMessage}
          </Text>
          {item.unread && (
            <View style={styles.unreadIndicator}>
              {item.unreadCount > 1 ? (
                <Text style={{ fontSize: 10, color: '#fff', fontWeight: '700' }}>
                  {item.unreadCount > 99 ? '99+' : item.unreadCount}
                </Text>
              ) : null}
            </View>
          )}
        </View>
      </View>
    </TouchableOpacity>
  );

  const renderEmptyList = () => (
    <View style={styles.emptyContainer}>
      <MaterialIcons name="chat-bubble-outline" size={60} color="#CBD5E1" />
      <Text style={styles.emptyTitle}>
        {error ? 'Could not load messages' : 'No messages yet'}
      </Text>
      <Text style={styles.emptySubtitle}>
        {error
          ? (error.message || 'Pull down to retry')
          : searchQuery
            ? 'Try adjusting your search terms'
            : activeTab === 'unread'
              ? 'You have no unread messages'
              : activeTab === 'archived'
                ? 'No archived conversations'
                : 'Your conversations with recruiters will appear here'}
      </Text>
    </View>
  );

  return (
    <SafeAreaView style={styles.safeArea}>
      <StatusBar barStyle="light-content" backgroundColor="#0e4e96" />

      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Ionicons name="arrow-back" size={24} color="#ffffff" />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Messages</Text>
        <TouchableOpacity
          style={styles.headerAction}
          onPress={() => navigation.navigate('NewMessage')}
        >
          <Ionicons name="create-outline" size={24} color="#ffffff" />
        </TouchableOpacity>
      </View>

      {/* Search Bar */}
      <View style={styles.searchContainer}>
        <Ionicons name="search" size={20} color="#94A3B8" style={styles.searchIcon} />
        <TextInput
          style={styles.searchInput}
          placeholder="Search messages..."
          placeholderTextColor="#94A3B8"
          value={searchQuery}
          onChangeText={setSearchQuery}
        />
        {searchQuery.length > 0 && (
          <TouchableOpacity
            style={styles.clearButton}
            onPress={() => setSearchQuery('')}
          >
            <Ionicons name="close-circle" size={20} color="#94A3B8" />
          </TouchableOpacity>
        )}
      </View>

      {/* Tabs */}
      <View style={styles.tabContainer}>
        <TouchableOpacity
          style={[styles.tab, activeTab === 'all' && styles.activeTab]}
          onPress={() => setActiveTab('all')}
        >
          <Text style={[styles.tabText, activeTab === 'all' && styles.activeTabText]}>
            All
          </Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={[styles.tab, activeTab === 'unread' && styles.activeTab]}
          onPress={() => setActiveTab('unread')}
        >
          <Text style={[styles.tabText, activeTab === 'unread' && styles.activeTabText]}>
            Unread
          </Text>
          {unreadTotal > 0 && (
            <View style={styles.badgeContainer}>
              <Text style={styles.badgeText}>{unreadTotal}</Text>
            </View>
          )}
        </TouchableOpacity>

        <TouchableOpacity
          style={[styles.tab, activeTab === 'archived' && styles.activeTab]}
          onPress={() => setActiveTab('archived')}
        >
          <Text style={[styles.tabText, activeTab === 'archived' && styles.activeTabText]}>
            Archived
          </Text>
        </TouchableOpacity>
      </View>

      {/* Conversation List */}
      {loading && conversations.length === 0 ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size={36} color="#0e4e96" />
          <Text style={styles.loadingText}>Loading messages...</Text>
        </View>
      ) : (
        <FlatList
          data={filtered}
          renderItem={renderConversationItem}
          keyExtractor={item => item.id}
          contentContainerStyle={styles.listContainer}
          ListEmptyComponent={renderEmptyList}
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={handlePullToRefresh}
              tintColor="#0e4e96"
              colors={['#0e4e96']}
            />
          }
        />
      )}

      {/* Bottom Navigation */}
      <View style={styles.bottomNav}>
        <TouchableOpacity
          style={styles.navItem}
          onPress={() => navigation.navigate('ProfessionalDashboard')}
        >
          <Ionicons name="home-outline" size={22} color="#64748B" />
          <Text style={styles.navLabel}>Home</Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={styles.navItem}
          onPress={() => navigation.navigate('FindJobs')}
        >
          <Ionicons name="briefcase-outline" size={22} color="#64748B" />
          <Text style={styles.navLabel}>Jobs</Text>
        </TouchableOpacity>
        <TouchableOpacity style={[styles.navItem, styles.navItemActive]}>
          <Ionicons name="chatbubble-ellipses" size={22} color="#0e4e96" />
          <Text style={[styles.navLabel, { color: '#0e4e96' }]}>Messages</Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={styles.navItem}
          onPress={() => navigation.navigate('Profile')}
        >
          <Ionicons name="person-outline" size={22} color="#64748B" />
          <Text style={styles.navLabel}>Profile</Text>
        </TouchableOpacity>
      </View>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: '#F8FAFC',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#0e4e96',
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  backButton: {
    padding: 8,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: '#ffffff',
  },
  headerAction: {
    padding: 8,
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#ffffff',
    margin: 16,
    marginTop: 12,
    marginBottom: 8,
    borderRadius: 8,
    paddingHorizontal: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 1,
  },
  searchIcon: {
    marginRight: 8,
  },
  searchInput: {
    flex: 1,
    height: 40,
    fontSize: 14,
    color: '#0F172A',
  },
  clearButton: {
    padding: 4,
  },
  tabContainer: {
    flexDirection: 'row',
    backgroundColor: '#ffffff',
    paddingHorizontal: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#E2E8F0',
  },
  tab: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    marginRight: 24,
  },
  activeTab: {
    borderBottomWidth: 2,
    borderBottomColor: '#0e4e96',
  },
  tabText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#64748B',
  },
  activeTabText: {
    color: '#0e4e96',
  },
  badgeContainer: {
    backgroundColor: '#EF4444',
    borderRadius: 10,
    paddingHorizontal: 6,
    paddingVertical: 2,
    marginLeft: 6,
  },
  badgeText: {
    fontSize: 10,
    fontWeight: '700',
    color: '#ffffff',
  },
  listContainer: {
    flexGrow: 1,
    paddingBottom: 70, // Space for bottom nav
  },
  conversationItem: {
    flexDirection: 'row',
    backgroundColor: '#ffffff',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#E2E8F0',
  },
  companyLogo: {
    width: 50,
    height: 50,
    borderRadius: 25,
    marginRight: 12,
  },
  conversationContent: {
    flex: 1,
  },
  conversationHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 4,
  },
  companyName: {
    fontSize: 16,
    fontWeight: '700',
    color: '#0F172A',
  },
  timestamp: {
    fontSize: 12,
    color: '#64748B',
  },
  positionName: {
    fontSize: 14,
    color: '#64748B',
    marginBottom: 6,
  },
  messageContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  lastMessage: {
    fontSize: 14,
    color: '#64748B',
    flex: 1,
    lineHeight: 20,
  },
  unreadMessage: {
    color: '#0F172A',
    fontWeight: '500',
  },
  unreadIndicator: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: '#0e4e96',
    marginLeft: 8,
  },
  interviewInfo: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  interviewDate: {
    fontSize: 12,
    color: '#64748B',
    marginLeft: 6,
  },
  emptyContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
    marginTop: 50,
  },
  emptyTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: '#0F172A',
    marginTop: 16,
    marginBottom: 8,
  },
  emptySubtitle: {
    fontSize: 14,
    color: '#64748B',
    textAlign: 'center',
    lineHeight: 20,
  },
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  loadingText: {
    fontSize: 14,
    color: '#64748B',
    marginTop: 12,
  },
  bottomNav: {
    flexDirection: 'row',
    backgroundColor: '#ffffff',
    paddingVertical: 10,
    borderTopWidth: 1,
    borderTopColor: '#E2E8F0',
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
  },
  navItem: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  activeNavItem: {
    borderTopWidth: 2,
    borderTopColor: '#0e4e96',
    marginTop: -2,
  },
  navText: {
    fontSize: 12,
    color: '#64748B',
    marginTop: 4,
  },
  activeNavText: {
    color: '#0e4e96',
    fontWeight: '600',
  },
  fab: {
    position: 'absolute',
    right: 20,
    bottom: 80,
    width: 56,
    height: 56,
    borderRadius: 28,
    backgroundColor: '#0e4e96',
    alignItems: 'center',
    justifyContent: 'center',
    elevation: 4,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2,
    shadowRadius: 4,
  },
});
